import { NgModule, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject } from '@angular/core';
import { provideAppInitializer } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as Sentry from '@sentry/angular';
import { environment } from '@environment/environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { IonicModule } from '@ionic/angular';
import { Amplify } from 'aws-amplify';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader, TRANSLATE_HTTP_LOADER_CONFIG } from '@ngx-translate/http-loader';
import { registerLocaleData } from '@angular/common';
import localeEn from '@angular/common/locales/en';
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';
import localePt from '@angular/common/locales/pt';
import localeUk from '@angular/common/locales/uk';

// NgRx State Module
import { StateModule } from './store/state.module';

import { AppComponent } from '@app/app.component';
import { AppRoutingModule } from '@app/app-routing.module';
import { SharedModule } from '@app/shared/modules/shared.module';
import { ToastNotificationService } from '@app/core/infrastructure';
import { SidenavComponent } from './components/sidenav/sidenav.component';
import { ControlHeaderComponent } from './components/control-header/control-header.component';
import { AuthInterceptor } from './core/interceptors/auth.interceptor';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { ConfirmationDialogComponent } from './popups/confirmation-dialog/confirmation-dialog.component';
import { ChangePasswordDialogComponent } from './popups/change-password-dialog/change-password-dialog.component';
import { LoadingContainerComponent } from './components/loading-container/loading-container.component';
import { registerBundledIcons } from './core/icons/icons.config';

registerLocaleData(localeEn, 'en');
registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeIt, 'it');
registerLocaleData(localePt, 'pt');
registerLocaleData(localeUk, 'uk');

export function initializeIconifyIcon() {
  return import('iconify-icon').then(() => {
    registerBundledIcons();
  }).catch(err => console.warn('Failed to load iconify-icon:', err));
}

export function configureAmplifyInitializer() {
  const isMobile = (window as any).Capacitor && (window as any).Capacitor.isNativePlatform();

  const getRedirectUrls = () => {
    if (isMobile) {
      return {
        redirectSignIn: [
          environment.oauth.redirectUri.web
        ],
        redirectSignOut: [
          environment.oauth.redirectUri.web.replace('/auth/callback', '/login')
        ]
      };
    } else {
      return {
        redirectSignIn: [environment.oauth.redirectUri.web],
        redirectSignOut: [environment.oauth.redirectUri.web.replace('/auth/callback', '/login')]
      };
    }
  };

  const redirectUrls = getRedirectUrls();

  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: environment.oauth.userPoolId,
        userPoolClientId: environment.oauth.clientId,
        loginWith: {
          oauth: {
            domain: environment.oauth.domain,
            scopes: environment.oauth.scopes,
            redirectSignIn: redirectUrls.redirectSignIn,
            redirectSignOut: redirectUrls.redirectSignOut,
            responseType: 'code'
          }
        }
      }
    }
  });
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    StateModule,
    IonicModule.forRoot(),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: TranslateHttpLoader
      }
    }),
    AppRoutingModule,
    SharedModule,
    SidenavComponent,
    ControlHeaderComponent,
    ConfirmationDialogComponent,
    ChangePasswordDialogComponent,
    LoadingContainerComponent,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: TRANSLATE_HTTP_LOADER_CONFIG,
      useValue: {
        prefix: './assets/i18n/',
        suffix: '.json'
      }
    },
    ToastNotificationService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true
    },
    provideAppInitializer(configureAmplifyInitializer),
    provideAppInitializer(initializeIconifyIcon),
    ...(environment.sentry?.enabled ? [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      provideAppInitializer(() => {
        inject(Sentry.TraceService);
      }),
    ] : [])
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule { }
